import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Box } from '@mui/material';
import {
  getEntitlements,
  getUserDetails,
  getUserFavorites,
  getUserFreeTrialEligible,
  getUserSettings,
  getUserEmailVerified,
  getUserSubscriptionWarnings,
} from '@surfline/web-common';

import {
  PREMIUM_PERKS,
  RELEASE_ANNOUNCEMENT,
  SL_WEB_ACCOUNT_SHARING_BANNER,
} from 'common/treatments';
import config from 'config';
import useConditions from 'hooks/useConditions';
import useGreenDeploy from 'hooks/useGreenDeploy';
import { getIsGuestUser, useUserPermissionStatus } from 'selectors/user';
import type { TaxonomyNavigatorSettings } from 'types/header';
import { useTreatments, parseTreatmentWithConfig } from 'utils/treatments';

import ErrorBoundary from '../ErrorBoundary';
import ErrorMessage from '../ErrorMessage';
import FavoritesBar from './components/FavoritesBar';
import NavigationBar from './components/NavigationBar';
import NotificationBarController from './components/NotificationBarController';
import { loadSavedNavigation } from './components/NavigationBar/components/TaxonomyNavigator/utils/navigationStore';

import styles from './Header.module.scss';

interface HeaderContentProps {
  isCTAVisible?: boolean;
  isFavoritesBarVisible?: boolean;
  isVisible?: boolean;
}

const HeaderContent: React.FC<HeaderContentProps> = ({
  isCTAVisible,
  isFavoritesBarVisible = true,
  isVisible,
}) => {
  const entitlements = useSelector(getEntitlements, shallowEqual);
  const favorites = useSelector(getUserFavorites, shallowEqual);
  const userDetails = useSelector(getUserDetails, shallowEqual);
  const userIsFreeTrialEligible = useSelector(getUserFreeTrialEligible, shallowEqual);
  const userIsVerified = useSelector(getUserEmailVerified, shallowEqual);
  const userSettings = useSelector(getUserSettings, shallowEqual);
  const isGuestUser = useSelector(getIsGuestUser);
  const userAccountSubsWarning = useSelector(getUserSubscriptionWarnings, shallowEqual) as any as {
    alert?: boolean;
    message?: string;
    type?: string;
  };
  const { themeClassName } = useConditions();
  // Check if the user is on the green deploy treatment and change the logo green to denote when the green deploy is active
  const isGreenDeploy = useGreenDeploy();

  const accountAlert = useMemo(() => userDetails && !userIsVerified, [userDetails, userIsVerified]);
  const accountWarning = userAccountSubsWarning?.alert;
  const spotsTaxonomyLocation = loadSavedNavigation('spotsTaxonomyLocation');
  const forecastsTaxonomyLocation = loadSavedNavigation('forecastsTaxonomyLocation');
  const navSettings = useMemo<TaxonomyNavigatorSettings>(
    () => ({
      units: userSettings?.units,
      navigation: {
        spotsTaxonomyLocation: spotsTaxonomyLocation ?? undefined,
        forecastsTaxonomyLocation: forecastsTaxonomyLocation ?? undefined,
      },
      recentlyVisited: userSettings?.recentlyVisited,
    }),
    [
      forecastsTaxonomyLocation,
      spotsTaxonomyLocation,
      userSettings?.recentlyVisited,
      userSettings?.units,
    ],
  );
  const treatments = useTreatments();
  const featureReleaseTreatment = treatments[RELEASE_ANNOUNCEMENT];
  const premiumPerksTreatment = treatments[PREMIUM_PERKS];
  const { hasAddMembersPermissions } = useUserPermissionStatus();
  const featureReleaseConfig = useMemo(
    () => parseTreatmentWithConfig(featureReleaseTreatment),
    [featureReleaseTreatment],
  );
  const premiumPerksConfig = useMemo(
    () => parseTreatmentWithConfig(premiumPerksTreatment),
    [premiumPerksTreatment],
  );
  const showManageMembersLink = !isGuestUser && hasAddMembersPermissions;

  const accountSharingBannerTreatment = treatments[SL_WEB_ACCOUNT_SHARING_BANNER];
  const accountSharingBannerConfig = useMemo(
    () => parseTreatmentWithConfig(accountSharingBannerTreatment),
    [accountSharingBannerTreatment],
  );
  return (
    <header
      className={classNames(styles.header, {
        [styles.headerHidden]: !isVisible,
        'sl-kbyg-web-green': isGreenDeploy,
      })}
      data-testid="header-content"
    >
      <NotificationBarController
        user={userDetails}
        warning={userAccountSubsWarning}
        entitlements={entitlements}
        accountSharingBannerConfig={accountSharingBannerConfig}
      />
      <NavigationBar
        user={userDetails}
        settings={navSettings}
        hideCTA={!isCTAVisible}
        entitlements={entitlements}
        serviceConfig={{
          serviceUrl: config.servicesURL,
        }}
        freeTrialEligible={userIsFreeTrialEligible}
        accountAlert={accountAlert}
        accountWarning={accountWarning}
        conditionColorsClass={themeClassName}
        userSettings={userSettings}
        latestFeatureRelease={featureReleaseConfig}
        latestPremiumPerkRelease={premiumPerksConfig}
        showManageMembersLink={showManageMembersLink}
      />
      {isFavoritesBarVisible && (
        <FavoritesBar settings={navSettings} favorites={favorites} user={userDetails} />
      )}
    </header>
  );
};

export interface HeaderProps extends HeaderContentProps {
  isError?: boolean;
}

export const Header: React.FC<HeaderProps> = (props) => {
  const { isError } = props;
  return (
    <div data-testid="header">
      <ErrorBoundary
        error={isError}
        errorBoundaryId="header"
        render={() => (
          <Box className={styles.headerError} data-testid="header-error">
            <ErrorMessage dataTestId="error-message" />
          </Box>
        )}
      >
        <HeaderContent {...props} />
      </ErrorBoundary>
    </div>
  );
};

export default Header;
